/**
 * The live `/quest-studio` container (#577 / R6.2) — creating and scheduling a family's quests.
 *
 * **What backs it**, measured against `origin/main`:
 *
 * | part | endpoint |
 * | --- | --- |
 * | packs | `GET /api/quest-packs` (`quest-packs.routes.js:52`, admin-only) |
 * | kids | `GET /api/admin/users?includeSelf=true` (`admin.routes.js:23`) |
 * | board | `GET /api/admin/chores` (`chore-admin.routes.js:65`) |
 * | save | `POST /api/admin/chores` (`chore-admin.routes.js:109`) |
 * | add/remove a pack | `PUT /api/quest-packs/:key/selection` (`quest-packs.routes.js:65`) |
 * | reorder a quest (#577) | `PUT /api/chore-order` (`dashboard.routes.js`) — the holder's whole list |
 * | move a quest to another day (#577) | `PUT /api/admin/chores/:id` with `nextExecutionTime` only |
 *
 * **The save is the part that had to change underneath this screen.** `handleSave` hands the host
 * `{name, icon, reward, xp, assignedKids, rotate, frequency, days, requiresApproval}` and then
 * refuses to say "Quest saved" unless the receipt echoes every field. Three of those had nowhere
 * to go until this work: `days` had no column at all, and `xp` and `requiresApproval` had columns
 * the write schema silently dropped. A parent could pick Monday and Thursday, read "Quest saved",
 * and have lost the choice.
 *
 * **What is NOT wired, and is named rather than half-built:**
 *
 *   · ~~**Pack contents.**~~ **BUILT (#577 L16.)** `GET /api/quest-packs/:key/templates` answers
 *     one pack's quests, and `onAddTemplate` turns one of them into an ordinary quest through the
 *     SAME confirmed create the save button uses. No migration was needed: `quest_pack_templates`
 *     has joined `chore_templates` since #26, and the catalog GET already read that pair to count
 *     and to pick its three icons. Only a way to ASK for the rows was missing.
 *   · ~~**The rotation preview.**~~ **BUILT (#577 L19-L22.)** `POST /api/admin/chores/rotation-preview`
 *     answers the next seven days for the DRAFT on screen. The strip is still not derived here —
 *     rotation advances one step per COMPLETION, and only the server's own `_calculateNextAssignee`
 *     knows that rule, so this asks rather than predicts. It asks with a POST because the input is
 *     three arrays and a nullable id; it writes nothing.
 *
 *     **The server refuses more often than it projects, and the screen prints the refusal.**
 *     `QuestStudio.jsx` draws any cell without a resolvable child as a rest day, so a
 *     half-projected week would state that a quest does not happen on a day it does. A random
 *     quest, an unassigned quest and a quest with no weekdays chosen therefore return no cells at
 *     all, plus a reason naming what the parent should change.
 * **Editing IS wired now**, and closing it needed a server repair as well as a screen one. The
 * write path for `schedule_days`, `xp` and `requires_approval` landed with the day picker; NEITHER
 * read path returned them, so a quest could be saved correctly and never read back. Pressing a
 * board tile asks `GET /api/admin/chores/:id` for the one quest, and saving an open quest goes to
 * `PUT /api/admin/chores/:id` rather than creating a second one.
 *
 * **The vanishing-warning trap**, the same one `VillageRoute` and `FamilyRoute` carry: going live
 * leaves fixture mode, which silently REMOVES `SnapshotNotice`. This route carries its own note.
 */
import React, { Suspense, lazy, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import LanternLoader from '../components/LanternLoader.jsx';
import { ApiError, api as defaultApi, apiUrl } from '../api/client.js';
import { TENANT_PATH } from '../api/session.js';
import { toMembers } from '../lib/familyMapping.js';
import { knownCurrency } from '../lib/heldMoney.js';
import { SOFT_READ_TIMEOUT_MS, TENANT_READ_TIMEOUT_MS, softRead, softReadAfter } from '../lib/softRead.js';
import {
  addedPackIds,
  toBoard,
  toCreateChoreBody,
  toEditorDraft,
  toKids,
  toPackDetails,
  toQuestPacks,
  toRotationPreviewBody,
  toUpdateChoreBody,
  withBoardArt,
  withIconArt,
} from '../lib/questStudioMapping.js';
import { useOptionalActor } from '../state/ActorContext.jsx';
import { isoWeekday, moveToDay, planReorder } from '../lib/questBoardMoves.js';

const QuestStudio = lazy(() => import('../pages/QuestStudio.jsx'));

/** Carried on the page, because leaving fixture mode removes the fixture banner. */
export const NOT_BUILT_NOTICE =
  'Live family data. Press a quest on the board to open and change it, or drag it or press Move '
  + 'to change its place. Press a pack to open it and add one quest. The rotation preview shows a week only when every day of it can be named.';

/**
 * The editor's own starting values for a NEW quest, stated here and handed to `QuestStudio` as its
 * defaults.
 *
 * They are in this file because the rotation preview has to ask about a quest that is not saved
 * and not open, and this container otherwise never learns what the editor is showing. Passing the
 * SAME values in as `defaultSelectedDays` / `defaultAssignedKids` / `defaultRotate` is what keeps
 * the two from drifting: a preview seeded from a second copy of the defaults would answer about a
 * week the parent is not looking at.
 */
export const NEW_QUEST_DAYS = ['mon', 'tue', 'wed', 'thu', 'fri'];
export const NEW_QUEST_ASSIGNED = [];
export const NEW_QUEST_ROTATE = true;

/** No week and no reason — what the screen shows before an answer, and after one that failed. */
const NO_PREVIEW = Object.freeze({ cells: [], reason: '' });

/** A kids list that keeps its identity, so the preview is not re-asked for on every render. */
const NO_KIDS = Object.freeze([]);

/** One cell the page can actually draw: a day, a label, and a stated skipped/not-skipped. */
const drawableCell = (c) => Boolean(c) && typeof c === 'object'
  && typeof c.id === 'string' && typeof c.date === 'string' && typeof c.skipped === 'boolean';

/**
 * What this container is willing to believe about a preview answer (WF5 finding, 2026-09-19).
 *
 * The whole design rests on the server answering a WHOLE week or none, because `QuestStudio.jsx`
 * draws any cell without a resolvable child as a rest day — so a six-of-seven week states that a
 * quest does not happen on a day it does. The first version read `payload.cells` and ignored
 * `payload.projectable` entirely, which left the rule enforced on one side of the wire only: a
 * contradictory answer, or one cell that was not an object, produced exactly the partial week the
 * rule forbids, and the page's own memo dropped the bad entry without a word.
 *
 * So a week is accepted only when the server CLAIMS one and every cell in it is drawable.
 * Anything else is no week at all — and a refusal keeps its reason, because that sentence is the
 * thing the parent can act on.
 */
function acceptedPreview(payload, kids) {
  if (!payload || payload.success !== true) return NO_PREVIEW;
  if (payload.projectable !== true) {
    return { cells: [], reason: typeof payload.reason === 'string' ? payload.reason : '' };
  }
  const cells = Array.isArray(payload.cells) ? payload.cells : [];
  if (cells.length === 0 || !cells.every(drawableCell)) return NO_PREVIEW;
  // And every named child must be one THIS screen can draw (WF5 finding). `QuestStudio.jsx` looks
  // a cell's `kidId` up in the member list and renders a miss as a rest day, so a week naming a
  // child who is not in that list produces the false "this quest does not happen today" the whole
  // feature exists to prevent. The server now refuses a draft naming a non-member, so reaching
  // this line means the two disagree — and a week both sides do not agree on is not drawable.
  const known = new Set((Array.isArray(kids) ? kids : []).map((k) => String(k && k.id)));
  if (cells.some((c) => c.kidId !== null && c.kidId !== undefined && !known.has(String(c.kidId)))) {
    return NO_PREVIEW;
  }
  return { cells, reason: '' };
}

const newPreviewDraft = () => ({
  days: NEW_QUEST_DAYS,
  assignedKids: NEW_QUEST_ASSIGNED,
  rotate: NEW_QUEST_ROTATE,
  currentAssigneeId: null,
});

export const OPEN_FAILED =
  "We couldn't open that quest just now — nothing was changed. Give it another moment.";

export const LOAD_FAILED =
  "We couldn't load Quest Studio just now — nothing was changed. Give it another moment.";

export const NOT_A_PARENT =
  'Quest Studio is a parent screen. Your account is a member account, so this page has nothing '
  + 'to show you.';

export function loadMessage(error) {
  if (error instanceof ApiError && error.status === 401) {
    return 'Your sign-in has expired — please sign in again.';
  }
  if (error instanceof ApiError && error.status === 403) return NOT_A_PARENT;
  return LOAD_FAILED;
}

/** A per-write id the pack endpoint uses to make a retry idempotent. */
function mutationId() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `m-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

/**
 * The save handler, built as a factory so it can be tested without rendering the editor.
 *
 * The receipt is returned ONLY after the POST resolves. `persistWithReceipt` treats a void or a
 * rejection as "not saved", which is the behaviour that keeps the screen from claiming a write it
 * did not make — so this must not optimistically build one.
 *
 * `quest` echoes the caller's own draft object, not a re-derived one. The screen compares the
 * echo field by field, and re-deriving it would let a mapping bug agree with itself.
 */
export function buildSave({ api = defaultApi, kids = [], now, reload } = {}) {
  return async function save(draft) {
    const body = toCreateChoreBody(draft, kids, now ?? new Date());
    const created = await api.post('/api/admin/chores', body);

    // `chore-admin.routes.js:129` answers `{ success: true, chore, message }` with `chore.id`.
    // Anything else is a response that did NOT create a quest, and the previous version turned
    // every one of them into a receipt with an invented `saved-<timestamp>` id — so the page said
    // "Quest saved" for a write that created nothing. A cross-model reviewer found it.
    const id = created && created.success === true && created.chore ? created.chore.id : null;
    if (!Number.isInteger(id) || id <= 0) {
      throw Object.assign(
        new Error('The quest was not saved — the server did not confirm a new quest'),
        { statusCode: 502 },
      );
    }

    // Only after the write is CONFIRMED. A reload on a failed create would throw away the
    // parent's draft and show them a board that never changed.
    if (typeof reload === 'function') reload();
    return { receiptId: String(id), status: 'saved', quest: draft };
  };
}

/**
 * The EDIT handler — `PUT /api/admin/chores/:id`.
 *
 * Deliberately a sibling of `buildSave` rather than a flag inside it. The two differ in the one
 * place that matters: this one must never create anything. A single handler with an `if (id)`
 * would make "the id went missing" silently mean "create a second quest", and a parent editing a
 * typo would end up with two.
 *
 * `current` carries the quest's stored `nextExecutionTime`, which `toUpdateChoreBody` needs to
 * decide whether a schedule change stranded the due date on a weekday the quest may no longer use.
 *
 * The receipt contract is `buildSave`'s, for the same reason: `QuestStudio.handleSave` refuses to
 * say "Quest saved" unless the receipt echoes every field of the draft it handed over.
 */
export function buildUpdate({ api = defaultApi, id, kids = [], reload } = {}) {
  return async function update(draft) {
    if (!Number.isInteger(id) || id <= 0) {
      throw Object.assign(
        new Error('That quest could not be saved — the screen lost track of which quest it was'),
        { statusCode: 400 },
      );
    }
    const body = toUpdateChoreBody(draft, kids);
    const saved = await api.put(`/api/admin/chores/${encodeURIComponent(id)}`, body);

    // `chore-admin.routes.js:378` answers `{ success: true, chore, message }`. Anything else is a
    // response that did NOT update the quest, and turning one into a receipt would make the page
    // say "Quest saved" for a write that changed nothing.
    //
    // The id must be THE SAME ONE, not merely a positive number. Accepting any id means a
    // response naming quest 42 confirms a save the parent made to quest 41, and the page reports
    // success for a change to a quest they never opened.
    const savedId = saved && saved.success === true && saved.chore ? saved.chore.id : null;
    if (savedId !== id) {
      throw Object.assign(
        new Error('The quest was not saved — the server did not confirm the change'),
        { statusCode: 502 },
      );
    }

    if (typeof reload === 'function') reload();
    return { receiptId: String(savedId), status: 'saved', quest: draft };
  };
}

/**
 * The pack add/remove handler.
 *
 * One PUT per pack that actually changed, carrying the revision the server last reported. A pack
 * whose state did not move sends nothing — the endpoint is a per-pack selection, not a bulk set,
 * and re-asserting an unchanged pack would burn a revision for no reason.
 */
/**
 * Add ONE quest out of an opened pack (#577 L16).
 *
 * This is the half the screen could not do before: adding a WHOLE pack has always worked through
 * `PUT /api/quest-packs/:key/selection`, but taking a single quest out of one had no path at all.
 * It needs no new write endpoint — a template becomes an ordinary quest, so it goes through the
 * SAME confirmed create `buildSave` uses, including its "the server did not confirm a new quest"
 * check. Reusing that is the point: a second create path would be a second place for the
 * invented-receipt defect to come back.
 *
 * What the draft deliberately does NOT carry:
 *   · **the icon.** `template.icon` is a catalog glyph name and `chores.icon_file` holds a
 *     filename from this family's own icons directory. Writing one into the other points at a
 *     file that does not exist. The parent picks an icon after adding.
 *   · **an assignee.** `toCreateChoreBody` turns an empty list into `no-assignment`, which is
 *     true. Guessing a child would be this screen deciding whose job it is.
 *   · **days.** No template states a weekday, so none is invented.
 */
export function buildAddTemplate({ api = defaultApi, kids = [], now, reload, requiresApproval } = {}) {
  const save = buildSave({ api, kids, now, reload });
  return async function addTemplate(template) {
    const t = template && typeof template === 'object' ? template : {};
    return save({
      name: typeof t.name === 'string' ? t.name : '',
      reward: typeof t.reward === 'number' ? t.reward : 0,
      assignedKids: [],
      rotate: false,
      frequency: 'weekly',
      days: [],
      // #580: the family's own approval choice, like any new quest. Unread ⇒ not sent, and the
      // column default decides, as it always did.
      ...(typeof requiresApproval === 'boolean' ? { requiresApproval } : {}),
    });
  };
}

export function buildPacksChange({ api = defaultApi, packsPayload, reload } = {}) {
  return async function change(nextIds) {
    const next = new Set(Array.isArray(nextIds) ? nextIds : []);
    const packs = packsPayload && Array.isArray(packsPayload.packs) ? packsPayload.packs : [];

    // The endpoint is a PER-PACK selection, not a bulk set, so a multi-pack change is several
    // writes. An earlier version reloaded only AFTER the loop: when a later PUT threw, the reload
    // was skipped and the screen kept showing a selection the server had already partly changed.
    // Counting what actually landed is what makes the reload correct in both directions.
    let written = 0;
    const receipts = [];
    try {
      for (const pack of packs) {
        const wanted = next.has(pack.id);
        if (wanted === Boolean(pack.added)) continue;
        const reply = await api.put(`/api/quest-packs/${encodeURIComponent(pack.id)}/selection`, {
          added: wanted,
          expectedRevision: String(pack.revision),
          mutationId: mutationId(),
        });
        written += 1;
        const selection = reply?.selection;
        if (reply?.success !== true || selection?.id !== pack.id || selection?.added !== wanted
          || typeof selection?.revision !== 'string' || !selection.revision.trim()) {
          throw new Error('The server did not confirm the pack selection');
        }
        receipts.push(`${selection.id}:${selection.revision}`);
      }
      return receipts.length > 0
        ? { receiptId: receipts.join(','), status: 'saved', addedPacks: Array.from(next) }
        : null;
    } finally {
      // Only when something really changed. Reloading after a run that wrote nothing would throw
      // away a draft for no reason.
      if (written > 0 && typeof reload === 'function') reload();
    }
  };
}

/* ------------------------------------------------------------ board moves (#577 rows 5 and 6) */

const unconfirmed = (what) => Object.assign(
  new Error(`The ${what} was not confirmed by the server`),
  { statusCode: 502, unconfirmed: true },
);

/**
 * Save one person's list order — `PUT /api/chore-order`.
 *
 * `reorderChores` answers `{ success: true, count }` (`server/services/chore.service.js`). Its
 * `count` is the length of the list it was SENT, not a count of rows written, so the `count` check
 * below only catches a malformed reply. It cannot detect a partial write (the write is one
 * transaction). That the list is the holder's whole list is `planReorder`'s job.
 */
export function buildReorderWrite({ api = defaultApi } = {}) {
  return async function reorder(orderedIds) {
    const saved = await api.put('/api/chore-order', { orderedChoreIds: orderedIds });
    if (!saved || saved.success !== true || saved.count !== orderedIds.length) throw unconfirmed('new order');
    return saved;
  };
}

/**
 * Move one quest's next due date — `PUT /api/admin/chores/:id` carrying `nextExecutionTime` and
 * NOTHING else. The quest's days, holder, reward and turn order stay exactly as they are, which is
 * what the board's own sentence promises.
 *
 * The reply must name the same quest AND land on the day the parent chose, read by the same clock
 * the board places cards by. Anything else is a move the screen cannot vouch for.
 */
export function buildDayMoveWrite({ api = defaultApi } = {}) {
  return async function moveDay(id, nextExecutionTime, isoDay) {
    const saved = await api.put(`/api/admin/chores/${encodeURIComponent(id)}`, { nextExecutionTime });
    const chore = saved && saved.success === true ? saved.chore : null;
    const due = chore ? new Date(chore.nextExecutionTime) : null;
    if (!chore || chore.id !== id || !due || Number.isNaN(due.getTime()) || isoWeekday(due) !== isoDay) {
      throw unconfirmed('move');
    }
    return chore;
  };
}

/** The calm sentence for a move that did not land. The card is already back when this shows. */
export function moveFailedMessage(title, error) {
  const back = `${title} is back where it was.`;
  if (error instanceof ApiError && error.status === 401) {
    return `Your sign-in has expired — please sign in again. ${back}`;
  }
  if (error instanceof ApiError && error.status === 403 && error.body && error.body.code === 'PARENT_REAUTH_REQUIRED') {
    return `${back} Moving it needs a grown-up to confirm it is you — press the move again to do that.`;
  }
  if (error instanceof ApiError && error.status === 403) return `${back} ${NOT_A_PARENT}`;
  // Only the reorder endpoint's 500 proves rollback: it has no fallible post-commit read.
  // Admin updates commit before reading the saved row, so their 500 (like a gateway failure or
  // dropped connection) leaves the saved state unknown.
  const refused = error instanceof ApiError && (error.status < 500
    || (error.status === 500 && error.path === '/api/chore-order'));
  if (refused) {
    return `We couldn't move ${title} just now. ${back} Nothing was changed.`;
  }
  return `We could not confirm that move, so ${title} is shown where it was. Reload the page to see what was saved.`;
}

/**
 * `GET /api/admin/chores/icons` → the `iconOptions` prop.
 *
 * `chores.icon_file` stores a plain FILENAME such as "vacuum.png", read from the family's own
 * icons directory (`chore-admin.service.js:1011`). The page's own default options are built-in
 * glyph ids ('dishes', 'broom', ...), and writing one of those into that column would point at a
 * file that does not exist. So the picker is fed real filenames, and the id a parent chooses is
 * the value the column takes.
 *
 * A picture the family uploaded or generated has a name the SERVER made up:
 * `icon_<ms>_<16 hex>.<ext>` (`chore-admin.service.js:1185`) or `ai_icon_<ms>_<16 hex>.png`
 * (`replicate.service.js:660`), and its `displayName` is that filename without the extension. The
 * server keeps no better name, so show the browser-local creation date and minute. Only
 * colliding minute labels gain seconds; exact-second ties also get a small numbered suffix.
 * Removing a collision can shorten that minute's labels, but unrelated pictures keep their
 * names. The id always stays the filename, so choosing one still saves that file (#577).
 */
const SERVER_NAMED_PICTURE = /^(?:ai_)?icon_(\d+)_[0-9a-f]{16}\.[a-z0-9]+$/i;

function pictureLabel(created, currentYear, includeSeconds = false) {
  const options = { day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' };
  if (created.getFullYear() !== currentYear) options.year = 'numeric';
  if (includeSeconds) options.second = '2-digit';
  return `Picture from ${new Intl.DateTimeFormat(undefined, options).format(created)}`;
}

function toIconOptions(payload) {
  const icons = payload && Array.isArray(payload.icons) ? payload.icons : [];
  const currentYear = new Date().getFullYear();
  const minuteGroups = new Map();
  const options = icons
    .filter((i) => i && typeof i.filename === 'string' && i.filename.trim() !== '')
    .map((i) => {
      const match = SERVER_NAMED_PICTURE.exec(i.filename);
      const parsed = match ? new Date(Number(match[1])) : null;
      const created = parsed && !Number.isNaN(parsed.getTime()) ? parsed : null;
      const option = { id: i.filename, created, label: created
        ? pictureLabel(created, currentYear) : i.displayName || i.filename };
      if (created) {
        if (!minuteGroups.has(option.label)) minuteGroups.set(option.label, []);
        minuteGroups.get(option.label).push(option);
      }
      return option;
    });
  for (const group of minuteGroups.values()) {
    if (group.length < 2) continue;
    const secondGroups = new Map();
    for (const option of group) {
      option.label = pictureLabel(option.created, currentYear, true);
      if (!secondGroups.has(option.label)) secondGroups.set(option.label, []);
      secondGroups.get(option.label).push(option);
    }
    // Two files can be created in the same second (even the same millisecond). Preserve
    // uniqueness without putting milliseconds or internal filenames back into the copy.
    for (const ties of secondGroups.values()) {
      if (ties.length < 2) continue;
      ties.sort((a, b) => a.created - b.created || (a.id < b.id ? -1 : a.id > b.id ? 1 : 0));
      ties.forEach((option, index) => { option.label += ` (${index + 1})`; });
    }
  }
  return options.map(({ id, label }) => ({ id, label }));
}

/**
 * How long the studio waits for ONE pack's contents before giving up on that pack.
 *
 * The pack panel is an extra. The board is the screen. This is the number that keeps a stalled
 * optional read from deciding when the screen appears.
 */
export const PACK_TEMPLATES_TIMEOUT_MS = SOFT_READ_TIMEOUT_MS;

// The pack list has to come back BEFORE its contents can be asked for, so this is a second
// round trip rather than a fifth parallel request above. The calls inside it ARE parallel.
//
// Every available pack is fetched up front because `QuestStudio.jsx` opens a pack only when
// `packDetails` already holds its templates (the `canOpen` guard on each pack card). Fetching
// on the click instead would need the shared component to grow a loading state for a panel,
// and these are four small reads of a global catalog. A pack whose read fails is simply absent
// from the map, so its card explains the failed read and keeps selection available.
//
// Each read is BOUNDED, and that bound is the load-bearing part. `Promise.all` waits for the
// slowest member, so without it one stalled template request holds back the board, the
// children and the pack catalog that all already answered — the screen shows a spinner
// because an OPTIONAL extra never came back. A cross-model reviewer found exactly that: the
// rejection path was covered and the never-settles path was not.
//
// A read that misses the deadline is treated the same as one that failed: its pack is absent
// from the map and its card stays closed. The request itself is not cancelled, because the
// shared `signal` belongs to the whole load and aborting it would take the rest down too.
function loadPackContents(packs, signal) {
  return Promise.all(packs.map((pack) => softRead(
    defaultApi
      .get(`/api/quest-packs/${encodeURIComponent(pack.id)}/templates`, { signal })
      .then((payload) => ({ key: pack.id, payload })),
    PACK_TEMPLATES_TIMEOUT_MS,
  )));
}

async function loadStudio(signal) {
  const required = Promise.all([
    defaultApi.get('/api/quest-packs', { signal }),
    defaultApi.get('/api/admin/users?includeSelf=true', { signal }),
    defaultApi.get('/api/admin/chores', { signal }),
    defaultApi.get('/api/admin/chores/icons', { signal }),
  ]);
  // The pack contents are the board's SECOND round (`loadPackContents`). It starts the moment the pack list
  // answers, beside the family read rather than after it.
  const contents = required.then(([packsPayload]) => loadPackContents(toQuestPacks(packsPayload), signal));
  const [[packsPayload, usersPayload, choresPayload, iconsPayload], answers, tenantPayload] = await Promise.all([
    required,
    contents,
    // #580: the family's currency and approval choice. A SOFT read: failing it, or not answering
    // within TENANT_READ_TIMEOUT_MS of the board's LAST required round (the pack contents), withholds
    // the unit and the family comparison, never the board. An answer that lands while the board is
    // still loading is kept, because the board could not have appeared any sooner (Qwen q3 M1).
    softReadAfter(defaultApi.get(TENANT_PATH, { signal }), contents, TENANT_READ_TIMEOUT_MS),
  ]);
  const tenant = tenantPayload && typeof tenantPayload.tenant === 'object' ? tenantPayload.tenant : null;
  const currency = knownCurrency(tenant ? tenant.currency : null);
  const kids = toKids(toMembers(usersPayload));
  const packs = toQuestPacks(packsPayload);

  return {
    packsPayload,
    packs,
    addedPacks: addedPackIds(packsPayload),
    kids,
    iconOptions: toIconOptions(iconsPayload),
    board: toBoard(choresPayload, kids),
    packDetails: toPackDetails(answers.filter(Boolean), currency),
    currency,
    familyRequiresApproval: tenant && typeof tenant.approvalRequired === 'boolean' ? tenant.approvalRequired : undefined,
  };
}

export default function QuestStudioRoute() {
  const [state, setState] = useState({ status: 'loading', data: null, error: null });
  const [reloadKey, setReloadKey] = useState(0);
  const nowRef = useRef(undefined);
  // Keep only a real family value, across soft read failures, for EVERY new-quest path.
  // The current read remains separate so the page can explain when this value is stale.
  const lastFamilyApproval = useRef(undefined);
  const moving = useRef(false);
  const reloadQueued = useRef(false);
  const refreshing = useRef(true);

  /**
   * The open quest, or `null` for "a new one".
   *
   * `null` is what puts the editor back in UNCONTROLLED mode: every controlled prop below becomes
   * `undefined`, and `QuestStudio`'s own `useControllable` falls back to its defaults. That is
   * why creating a quest needs no separate branch — it is simply the absence of an open one.
   */
  const [editing, setEditing] = useState(null);
  const [openError, setOpenError] = useState(null);

  /**
   * What the rotation preview is asking about.
   *
   * It is mirrored rather than read out of `editing`, because a NEW quest lives entirely inside
   * `QuestStudio`'s own uncontrolled state — this container never sees those values, and the
   * preview must work before anything is saved. The change handlers below feed both.
   */
  const [previewDraft, setPreviewDraft] = useState(newPreviewDraft);
  const [preview, setPreview] = useState(NO_PREVIEW);

  useEffect(() => {
    const controller = new AbortController();
    let live = true;
    setState((prev) => ({ ...prev, status: 'loading', error: null }));
    loadStudio(controller.signal)
      .then((data) => {
        if (live) {
          if (typeof data.familyRequiresApproval === 'boolean') lastFamilyApproval.current = data.familyRequiresApproval;
          refreshing.current = false;
          setState({ status: 'ready', data, error: null });
        }
      })
      .catch((error) => {
        if (!live || controller.signal.aborted) return;
        setState({ status: 'error', data: null, error });
      });
    return () => { live = false; controller.abort(); };
  }, [reloadKey]);

  const reload = useCallback(() => {
    if (moving.current) { reloadQueued.current = true; return; }
    refreshing.current = true;
    setReloadKey((n) => n + 1);
  }, []);
  const data = state.data;

  /**
   * Pressing a board tile opens that quest.
   *
   * It asks for the ONE quest rather than reusing the board row, because `GET /api/admin/chores`
   * does not return `scheduleDays`, `xp` or `requiresApproval` — filling the editor from the board
   * would show a parent a day picker, an XP value and an approval switch that are not the ones
   * they saved.
   */
  const onTileSelect = useCallback(async (tile) => {
    const id = tile && tile.id;
    if (!Number.isInteger(id) || id <= 0) return;
    setOpenError(null);
    try {
      const payload = await defaultApi.get(`/api/admin/chores/${encodeURIComponent(id)}`);
      const chore = payload && payload.success === true ? payload.chore : null;
      // `toEditorDraft` throws on any payload that is not a COMPLETE chore. A partly-filled draft
      // would look like a real quest with fields cleared, and saving it would wipe the quest just
      // opened.
      const draftFor = toEditorDraft(chore);
      // And it must be the quest that was asked for. Loading 42's fields while holding 41's id
      // means the next save writes 42's values over quest 41.
      if (draftFor.id !== id) {
        throw Object.assign(new Error('the server returned a different quest'), { statusCode: 502 });
      }
      setEditing({ id, draft: draftFor });
      // Including who holds it TODAY, so the strip carries on from the real holder rather than
      // restarting at the top of the list.
      setPreviewDraft({
        days: draftFor.days,
        assignedKids: draftFor.assignedKids,
        rotate: draftFor.rotate,
        currentAssigneeId: draftFor.currentAssigneeId,
      });
    } catch {
      setEditing(null);
      setOpenError(OPEN_FAILED);
    }
  }, []);

  /** One field of the open draft. A no-op when nothing is open, so a stray change cannot create one. */
  const setField = useCallback((key) => (value) => {
    setEditing((prev) => (prev ? { ...prev, draft: { ...prev.draft, [key]: value } } : prev));
  }, []);

  /**
   * The three controls the rotation preview depends on: days, assignees and the rotate switch.
   *
   * These write the mirror as well as the open draft, and the mirror is the half that matters for
   * a NEW quest — `setField` deliberately does nothing when nothing is open, so on its own the
   * preview would never move until a parent saved.
   */
  const setPreviewField = useCallback((key) => {
    const openDraft = setField(key);
    return (value) => {
      openDraft(value);
      setPreviewDraft((prev) => ({ ...prev, [key]: value }));
    };
  }, [setField]);

  /** Leaving the editor. `QuestStudio` resets its own uncontrolled controls; this drops the id. */
  const onDiscard = useCallback(() => {
    setEditing(null);
    setOpenError(null);
    // The editor returns to its own defaults, so the preview has to return to the same ones.
    setPreviewDraft(newPreviewDraft());
  }, []);

  const draft = editing ? editing.draft : null;

  const onSave = useMemo(
    () => (editing
      ? buildUpdate({ id: editing.id, kids: data ? data.kids : [], reload })
      : buildSave({ kids: data ? data.kids : [], now: nowRef.current, reload })),
    [editing, data, reload],
  );
  const onPacksChange = useMemo(
    () => buildPacksChange({ packsPayload: data ? data.packsPayload : null, reload }),
    [data, reload],
  );
  const onAddTemplate = useMemo(
    () => buildAddTemplate({
      kids: data ? data.kids : [], now: nowRef.current, reload, requiresApproval: lastFamilyApproval.current,
    }),
    [data, reload],
  );

  /**
   * The preview request.
   *
   * It waits for `data`, because the body filters the assigned ids against this family's real
   * members and an unfiltered list would ask about children who are not there.
   *
   * A failure is an ABSENT preview, never a stale one: the previous week is cleared rather than
   * left on screen next to a draft it no longer describes. The preview is an extra, so a failure
   * must not reach the page-level error either — the board is the screen.
   */
  // Keyed on the KIDS list rather than on `data`, so a reload that returns an equal member list
  // does not re-ask for a week nothing about the draft changed.
  const kids = data ? data.kids : NO_KIDS;
  const previewBody = useMemo(
    () => toRotationPreviewBody(previewDraft, kids),
    [previewDraft, kids],
  );

  useEffect(() => {
    if (!data) return undefined;
    const controller = new AbortController();
    let live = true;
    defaultApi.post('/api/admin/chores/rotation-preview', previewBody, { signal: controller.signal })
      .then((payload) => { if (live) setPreview(acceptedPreview(payload, kids)); })
      .catch((error) => {
        if (!live) return;
        // An expired session is NOT a quiet absence (WF5 finding). This endpoint is admin-only, so
        // a 401 or a 403 means the parent is looking at a screen their account can no longer act
        // on — swallowing that leaves them pressing controls that will fail. Everything else stays
        // silent on purpose: the board is the screen, and a network blip must not take it down.
        if (error instanceof ApiError && (error.status === 401 || error.status === 403)) {
          setOpenError(loadMessage(error));
        }
        setPreview(NO_PREVIEW);
      });
    return () => { live = false; controller.abort(); };
  }, [previewBody, data, kids]);

  /**
   * The board as shown. A move replaces it at once (optimistic), and a failed write puts back the
   * board from BEFORE the move.
   *
   * Reloads wait for the write to settle; moves wait for a pending reload. A read made during
   * a write is not necessarily newer than that write, so neither may replace the other's snapshot.
   */
  const [boardOverride, setBoardOverride] = useState({ from: null, board: null });
  const shownBoard = data && boardOverride.from === data ? boardOverride.board : (data ? data.board : null);
  // #577 line 62. A household picture's address needs the family's slug, which the signed-in
  // actor carries (`session.js`). Added at render, not at load, because the actor can arrive after
  // the payloads do. Without a slug no address is built: the page shows a glyph, never a guess.
  const tenantSlug = useOptionalActor()?.actor?.tenantSlug ?? null;
  // Wrapped with `apiUrl`, the one place that knows the API origin. Portraits are NOT: they stay
  // the server's `avatarUrl` verbatim, exactly as the Family screen shows them, and one may be an
  // absolute address already (a generated https portrait) that a prefix would break.
  // Root-relative portraits assume a same-origin API: the avatar route needs the session cookie.
  // app/nginx.conf records the unused split-origin setup; familyMapping.js, dashboardMapping.js
  // and deviceBoardMapping.js likewise preserve the server avatarUrl.
  const iconOptions = useMemo(() => withIconArt(data ? data.iconOptions : [], tenantSlug, apiUrl), [data, tenantSlug]);
  const boardWithArt = useMemo(() => withBoardArt(shownBoard, tenantSlug, apiUrl), [shownBoard, tenantSlug]);
  const [movePending, setMovePending] = useState(false);
  const [boardNotice, setBoardNotice] = useState('');
  const [boardNoticeKey, setBoardNoticeKey] = useState(0);
  const reportBoardNotice = useCallback((message) => {
    setBoardNotice(message);
    setBoardNoticeKey((key) => key + 1);
  }, []);
  const latest = useRef({ board: null, data: null });
  latest.current = { board: shownBoard, data };

  const onMoveTile = useCallback(async (intent) => {
    const { board: before, data: from } = latest.current;
    if (moving.current || refreshing.current || !before || !from || !intent) return;
    const day = typeof intent.toColumnId === 'string';
    let plan;
    try {
      plan = day
        ? moveToDay(before, intent.tileId, intent.toColumnId, new Date(), from.kids)
        : planReorder(before, intent.tileId, { direction: intent.direction, targetId: intent.targetId });
    } catch {
      // A card this screen cannot plan a move for (a due date that will not read, say). Nothing
      // was sent, so saying so is true, and a sentence beats an unhandled rejection.
      reportBoardNotice('That quest could not be moved, so nothing was changed. Reload the page and try again.');
      return;
    }
    if (!plan) return;
    if (plan.refusal) { reportBoardNotice(plan.refusal); return; }

    const title = plan.tile.title || 'That quest';
    moving.current = true;
    setMovePending(true);
    reportBoardNotice('');
    setBoardOverride({ from, board: plan.board });
    try {
      if (day) {
        await buildDayMoveWrite()(plan.tile.id, plan.nextExecutionTime, plan.toIsoDay);
        reportBoardNotice(`${title} is now due on ${plan.toDay}.`);
      } else {
        await buildReorderWrite()(plan.orderedIds);
        reportBoardNotice(`${title} moved.`);
      }
    } catch (error) {
      setBoardOverride((prev) => (prev.from === from ? { from, board: before } : prev));
      reportBoardNotice(moveFailedMessage(title, error));
    } finally {
      moving.current = false;
      setMovePending(false);
      if (reloadQueued.current) {
        reloadQueued.current = false;
        reload();
      }
    }
  }, [reload, reportBoardNotice]);

  const loadError = state.status === 'error' ? loadMessage(state.error) : null;

  return (
    <Suspense fallback={<LanternLoader label="Loading Quest Studio…" />}>
      <QuestStudio
        showSnapshotNotice={false}
        lead={NOT_BUILT_NOTICE}
        packs={data ? data.packs : []}
        addedPacks={data ? data.addedPacks : []}
        kids={data ? data.kids : []}
        iconOptions={iconOptions}
        board={boardWithArt}
        // #577 rows 5 and 6. Offered only once a family's board is loaded: a move needs the
        // server's own ids, holders and days, which a loading screen does not have yet.
        onMoveTile={data ? onMoveTile : undefined}
        movePending={movePending || state.status === 'loading'}
        boardNotice={boardNotice}
        boardNoticeKey={boardNoticeKey}
        onSave={onSave}
        onDiscard={onDiscard}
        onTileSelect={onTileSelect}
        onPacksChange={onPacksChange}
        // Controlled ONLY while a quest is open. `undefined` hands each control back to
        // `QuestStudio`'s own `useControllable` default, which is what "a new quest" means.
        questName={draft ? draft.name : undefined}
        selectedIcon={draft ? draft.icon : undefined}
        reward={draft ? draft.reward : undefined}
        assignedKids={draft ? draft.assignedKids : undefined}
        rotate={draft ? draft.rotate : undefined}
        frequency={draft ? draft.frequency : undefined}
        selectedDays={draft ? draft.days : undefined}
        requiresApproval={draft ? draft.requiresApproval : undefined}
        // #580. New quests use the last real family choice; never read ⇒ explained ON fallback.
        defaultRequiresApproval={lastFamilyApproval.current ?? true}
        familyRequiresApproval={data ? data.familyRequiresApproval : undefined}
        // `null` once loaded with no known currency: the stepper then shows no unit at all.
        currency={data ? data.currency : null}
        // Stated rather than left to the page, because the rotation preview is seeded from these
        // exact values. Two copies of "what a new quest starts as" would let the strip describe a
        // week the parent is not looking at.
        defaultSelectedDays={NEW_QUEST_DAYS}
        defaultAssignedKids={NEW_QUEST_ASSIGNED}
        defaultRotate={NEW_QUEST_ROTATE}
        // The page DERIVES the XP it shows from the reward (`QuestStudio.jsx:925-932`). For an open
        // quest that derived number is not the family's stored one, so the editor would display an
        // XP the quest does not have. Overriding the derivation with the stored value makes the
        // display true. `undefined` for a quest whose XP never came back leaves the page's own
        // derivation in place, which is the honest answer when there is nothing to show instead.
        xpFromReward={draft && Number.isInteger(draft.xp) ? () => draft.xp : undefined}
        onQuestNameChange={setField('name')}
        onIconChange={setField('icon')}
        onRewardChange={setField('reward')}
        onAssignedChange={setPreviewField('assignedKids')}
        onRotateChange={setPreviewField('rotate')}
        onFrequencyChange={setField('frequency')}
        onDaysChange={setPreviewField('days')}
        onApprovalChange={setField('requiresApproval')}
        // #577 L16. A map of packId → contents, built from one read per available pack. A pack
        // that did not answer is ABSENT from the map rather than present and empty, so its card
        // stays closed. `null` while nothing is loaded, because `{}` would claim the read
        // happened and found nothing.
        packDetails={data ? data.packDetails : null}
        onAddTemplate={onAddTemplate}
        // #577 L19-L22. The SERVER's own projection, never a client re-derivation: rotation
        // advances one step per COMPLETION, and re-deriving it here would be this screen guessing
        // at `_calculateNextAssignee`. `previewReason` carries the refusal for the days it will
        // not name a child for, and the page prints that instead of the strip.
        preview={preview.cells}
        previewReason={preview.reason}
        loading={state.status === 'loading' && !data}
        error={loadError || openError}
        onRetry={reload}
      />
    </Suspense>
  );
}
